import React from "react";
import { toast } from "react-toastify";
import GenericFormDialog, { Field } from "../GenericFormDialog";
import {
  createPackage,
  updatePackage,
} from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { SessionPackage } from "./types";
import { ACTIVE_OPTIONS, fromActiveLabel, toActiveLabel } from "./classUtils";
import { Option } from "./useClassOptions";

interface PackageFormDialogProps {
  open: boolean;
  sessionPackage: SessionPackage | null;
  categoryOptions: Option[];
  onClose: () => void;
  onSaved: () => void;
}

const SCOPE_OPTIONS = [
  { label: "Any class", value: "ALL" },
  { label: "One category", value: "CATEGORY" },
];

/**
 * Scope is create-only: the API's update payload cannot move a package between
 * categories, so the edit form hides those two fields.
 */
const PackageFormDialog: React.FC<PackageFormDialogProps> = ({
  open,
  sessionPackage,
  categoryOptions,
  onClose,
  onSaved,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [scopeType, setScopeType] = React.useState<string>("ALL");
  const isEdit = Boolean(sessionPackage);

  React.useEffect(() => {
    if (open) setScopeType(String(sessionPackage?.scope_type || "ALL"));
  }, [open, sessionPackage]);

  const fields: Field[] = React.useMemo(() => {
    const base: Field[] = [
      { name: "name", label: "Package Name", required: true },
      { name: "price", label: "Price", type: "number", required: true },
      {
        name: "session_count",
        label: "Sessions Included",
        type: "number",
        required: true,
      },
      {
        name: "validity_days",
        label: "Valid For (days)",
        type: "number",
        required: true,
      },
      {
        name: "active",
        label: "Status",
        type: "dropdown",
        options: ACTIVE_OPTIONS,
        required: true,
      },
      { name: "description", label: "Description", colSpan: 2 },
    ];

    if (isEdit) return base;

    const scopeFields: Field[] = [
      {
        name: "scope_type",
        label: "Scope",
        type: "dropdown",
        options: SCOPE_OPTIONS,
        required: true,
      },
    ];

    if (scopeType === "CATEGORY") {
      scopeFields.push({
        name: "category_id",
        label: "Category",
        type: "dropdown",
        options: categoryOptions,
        searchable: true,
        required: true,
      });
    }

    return [base[0], ...scopeFields, ...base.slice(1)];
  }, [categoryOptions, isEdit, scopeType]);

  const initialValues = React.useMemo(() => {
    if (sessionPackage) {
      return {
        name: String(sessionPackage.name || ""),
        price: String(sessionPackage.price ?? ""),
        session_count: String(sessionPackage.session_count ?? ""),
        validity_days: String(sessionPackage.validity_days ?? ""),
        description: String(sessionPackage.description || ""),
        active: toActiveLabel(sessionPackage.active),
      };
    }
    return {
      name: "",
      scope_type: "ALL",
      category_id: "",
      price: "",
      session_count: "",
      validity_days: "",
      description: "",
      active: "Active",
    };
  }, [sessionPackage]);

  const handleSubmit = async (values: Record<string, any>) => {
    const price = Number(values.price);
    const sessionCount = Number(values.session_count);
    const validityDays = Number(values.validity_days);

    if (!Number.isFinite(price) || price < 0) {
      toast.error("Price must be a positive number.");
      return;
    }
    if (!Number.isInteger(sessionCount) || sessionCount < 1) {
      toast.error("Sessions included must be a whole number of at least 1.");
      return;
    }
    if (!Number.isInteger(validityDays) || validityDays < 1) {
      toast.error("Validity must be a whole number of days, at least 1.");
      return;
    }

    setSubmitting(true);
    try {
      if (sessionPackage) {
        await updatePackage(String(sessionPackage.id), {
          name: String(values.name).trim(),
          description: values.description ? String(values.description) : undefined,
          price,
          session_count: sessionCount,
          validity_days: validityDays,
          active: fromActiveLabel(values.active),
        });
        toast.success("Package updated successfully!");
      } else {
        const scope = String(values.scope_type || "ALL") as "ALL" | "CATEGORY";
        if (scope === "CATEGORY" && !values.category_id) {
          toast.error("Pick the category this package covers.");
          setSubmitting(false);
          return;
        }
        await createPackage({
          name: String(values.name).trim(),
          description: values.description ? String(values.description) : undefined,
          price,
          session_count: sessionCount,
          validity_days: validityDays,
          scope_type: scope,
          category_id:
            scope === "CATEGORY" ? String(values.category_id) : undefined,
          active: fromActiveLabel(values.active),
        });
        toast.success("Package created successfully!");
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save package."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <GenericFormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit Package" : "Add Package"}
      submitLabel={isEdit ? "Save" : "Create"}
      fields={fields}
      initialValues={initialValues}
      isSubmitting={submitting}
      onValuesChange={(values) => {
        const nextScope = String(values.scope_type || "ALL");
        setScopeType((prev) => (prev === nextScope ? prev : nextScope));
      }}
      onSubmit={handleSubmit}
    />
  );
};

export default PackageFormDialog;
